// @givmo/app-charity — the "Name on Givmo" card (CPA-BE44, Charity Portal design 20 §5, §7).
//
// A claimed, consenting charity's Owner may set the name donors see on Givmo, its display name. The
// IRS legal name and the EIN stand beside it, read-only. The card reads the profile's `display_name`
// block and writes through two `/charity-admin/charities/{id}/display-name` routes:
//   * PUT — a name the look-alike check clears goes live at once; one it holds answers 409
//     `CHARITY_DISPLAY_NAME_NEEDS_REVIEW` with a sentence saying what it is close to (letters from
//     outside the Latin alphabet included, and a well-known charity's name), and the card offers
//     "Submit for review", the same PUT with `submit_for_review`; one it refuses answers 422
//     `CHARITY_DISPLAY_NAME_REFUSED`; a charity that cannot carry one now answers 409
//     `CHARITY_DISPLAY_NAME_NOT_AVAILABLE`. Every sentence is the server's, shown as it is. One rule
//     for the catalog name typed back (D-CP-109, `isCatalogName`): beside a live name it is sent, and
//     the backend ends the display name, withdrawing a request waiting beside it; with none live it
//     would change nothing, so the card says it is already the organization's name. Any other name,
//     the IRS legal name in capitals included, is a new display name for the check;
//   * DELETE `…/request` — "Withdraw request" takes back a request waiting for review and nothing else
//     (CPA-BE65): a live name beside it stays as it is.
// A charity can always end its own live name (CPA-BE79): beside one the field stays whatever the
// block's `eligible` says, and while the charity cannot carry a display name the card takes only the
// catalog name back, which the backend honours before its eligibility check. Beside a live name each
// reason is said its live-name way (`ineligibleLiveParts`): why no new name, the next step, and how
// to switch the live one off; the backend's own sentences stay for a charity with none live.
// Which reviewer decision the card shows, and why, is `display-name-decode.ts`'s header.
//
// The PUT is dark behind the backend's `CHARITY_DISPLAY_NAME_ENABLED`; with it off the profile
// carries no block and the panel renders no card at all. A Member sees the names, read-only, and
// beside a live name since when it is live and the line that says the paperwork keeps the catalog
// name.

import { useEffect, useId, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  charityAdminSetCharityDisplayNameMutation,
  charityAdminWithdrawCharityDisplayNameRequestMutation,
} from '@givmo/api-client/query';
import { firstErrorCode, isGivmoError, type CharityDisplayNameStateOut } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  cn,
  FOCUS_RING,
  Icon,
  TextField,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { Clock, Info, MessageSquare } from 'lucide-react';
import { useUnsavedChanges } from '../../lib/unsaved-changes';
import {
  decisionToShow,
  displayNameState,
  formatDay,
  formatEin,
  ineligibleLiveParts,
  ineligibleParts,
  isCatalogName,
  legalNameLabel,
  switchOffSentence,
  type SentencePart,
} from './display-name-decode';

const PREFIX = 'profile-display-name';

const NEEDS_REVIEW = 'CHARITY_DISPLAY_NAME_NEEDS_REVIEW';
const REFUSED = 'CHARITY_DISPLAY_NAME_REFUSED';
const NOT_AVAILABLE = 'CHARITY_DISPLAY_NAME_NOT_AVAILABLE';
const GENERIC_FAILURE = 'We couldn’t save your display name. Please try again.';

/** What the last Save answered, while it still describes the field. */
type Outcome =
  | { kind: 'held'; name: string; sentence: string }
  | { kind: 'refused'; sentence: string }
  | { kind: 'unavailable'; sentence: string }
  // The catalog name typed back with no display name live: answered here, never sent.
  | { kind: 'catalog' };

export interface DisplayNameCardProps {
  charityId: string;
  /** The name donors see now: the profile's `name`. */
  name: string;
  block: CharityDisplayNameStateOut;
  /** An Owner writes; a Member reads. */
  canEdit: boolean;
  /** Re-read what the write changed: the profile, and the memberships the console header names. */
  onChanged: () => Promise<unknown>;
}

export function DisplayNameCard({
  charityId,
  name,
  block,
  canEdit,
  onChanged,
}: DisplayNameCardProps) {
  const state = displayNameState(block);
  const live = state === 'live';
  const pendingName = block.pending_name?.trim() || null;
  const decision = decisionToShow(block);
  // The field: while the charity can carry a display name, and beside a live one whatever it can.
  const showField = canEdit && (block.eligible || live);
  // CPA-BE79: a charity that cannot carry a display name now can still end its live one, and only
  // that: the field then takes the catalog name back and nothing else.
  const endOnly = showField && !block.eligible;

  const [value, setValue] = useState(name);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const sameAsCatalogId = useId();
  const onlyCatalogId = useId();
  const goBackId = useId();
  // Where focus goes when a Withdraw takes its button away: the field, else the card's heading.
  const inputRef = useRef<HTMLInputElement>(null);
  const headingRef = useRef<HTMLSpanElement>(null);
  // A re-read after a write brings the new name; the field follows it.
  useEffect(() => {
    setValue(name);
  }, [name]);
  // A 409 "not available" re-reads the profile, and once the block it brings says so itself
  // (`ineligible_reason`), its sentence is the one the card shows.
  useEffect(() => {
    if (!block.eligible) {
      setOutcome((current) => (current?.kind === 'unavailable' ? null : current));
    }
  }, [block.eligible]);

  const typed = value.trim();
  const dirty = typed.length > 0 && typed !== name;
  // What Save would send: any edit, or only the catalog name back while the charity can carry none.
  const typedCatalog = isCatalogName(typed, block.catalog_name);
  const savable = dirty && (!endOnly || typedCatalog);
  // An edit Save cannot send says so at the field, where a grey Save would otherwise leave it unsaid.
  const onlyCatalog = endOnly && dirty && !typedCatalog;
  // While the last answer still describes the field, saving it again would only repeat that answer:
  // the next step is the notice's own (Submit for review), or a different name.
  const answered =
    outcome?.kind === 'held' || outcome?.kind === 'refused' || outcome?.kind === 'catalog';
  useUnsavedChanges(showField && savable);

  const save = useMutation({
    ...charityAdminSetCharityDisplayNameMutation(),
    onSuccess: async (data, variables) => {
      setOutcome(null);
      const next = data?.display_name;
      const submitted =
        variables.body.submit_for_review === true || next?.state === 'pending_review';
      // A request for review changes no name, so nothing re-seeds the field: put back the name
      // donors see, as a reload shows it. A name that did change comes in through the re-read.
      if (submitted) setValue(name);
      await onChanged();
      if (submitted) {
        toast.success('Submitted for review. We’ll show it to donors once it’s approved.');
      } else if (next?.state === 'live') {
        toast.success('Your display name is live.');
      } else if (live) {
        // The catalog name typed back: the backend ended the display name, and withdrew a request
        // waiting beside it.
        toast.success(
          pendingName !== null
            ? 'Your display name is off, and your request for review is withdrawn.'
            : 'Your display name is off.',
        );
      } else {
        toast.success('Saved.');
      }
    },
    onError: (error, variables) => {
      const sentence = isGivmoError(error) ? error.message : GENERIC_FAILURE;
      const code = isGivmoError(error) ? firstErrorCode(error) : null;
      if (code === NEEDS_REVIEW) {
        setOutcome({ kind: 'held', name: variables.body.display_name, sentence });
      } else if (code === REFUSED) {
        setOutcome({ kind: 'refused', sentence });
      } else if (code === NOT_AVAILABLE) {
        setOutcome({ kind: 'unavailable', sentence });
        void onChanged();
      } else {
        toast.error(sentence);
      }
    },
  });

  // The notice and its Withdraw are gone once the re-read lands, and focus with them: a keyboard
  // user's place goes to the field (or the heading), never back to the top of the page.
  function focusAfterNotice() {
    (inputRef.current ?? headingRef.current)?.focus();
  }

  // "Withdraw request": the request alone, so a live name beside it stays as it is (CPA-BE65).
  const withdraw = useMutation({
    ...charityAdminWithdrawCharityDisplayNameRequestMutation(),
    onSuccess: async () => {
      setOutcome(null);
      // Withdrawing changes no name either: a field still holding the request goes back to the
      // name. A name typed since is the Owner's new edit, and stays.
      if (value.trim() === pendingName) setValue(name);
      await onChanged();
      focusAfterNotice();
      toast.success('Request withdrawn.');
    },
    onError: async (error) => {
      toast.error(isGivmoError(error) ? error.message : 'Something went wrong. Please try again.');
      // No request waits any more (a reviewer decided it meanwhile): read what the card now shows.
      if (isGivmoError(error) && firstErrorCode(error) === NOT_AVAILABLE) {
        await onChanged();
        focusAfterNotice();
      }
    },
  });

  const busy = save.isPending || withdraw.isPending;
  const submitting = save.isPending && save.variables?.body.submit_for_review === true;
  const decidedOn = decision !== null ? formatDay(decision.decidedAt) : null;
  const liveSince = live ? formatDay(block.live_since) : null;

  function ask(submitForReview: boolean, displayName: string) {
    save.mutate({
      path: { charity_id: charityId },
      body: { display_name: displayName, submit_for_review: submitForReview },
    });
  }

  function saveTyped() {
    if (!showField || !savable || answered || busy) return;
    // Beside a live name the catalog name typed back is sent: it switches the display name off.
    if (!live && typedCatalog) {
      setOutcome({ kind: 'catalog' });
      return;
    }
    ask(false, typed);
  }

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    saveTyped();
  }

  const ein = formatEin(block.ein);
  const legalName = block.irs_legal_name?.trim() || null;
  const sameAsCatalog = outcome?.kind === 'catalog';
  const describedBy = [
    sameAsCatalog ? sameAsCatalogId : null,
    onlyCatalog ? onlyCatalogId : null,
    endOnly ? goBackId : null,
  ]
    .filter((id): id is string => id !== null)
    .join(' ');
  // Since when the name is live reads as a fact about the name, on its own line, not as fine print.
  const liveLines = live ? (
    <>
      {liveSince ? (
        <p className="text-s13 text-text-secondary" {...testIdProp(`${PREFIX}-live-since`)}>
          Live since {liveSince}.
        </p>
      ) : null}
      <p className="text-s12 text-text-tertiary" {...testIdProp(`${PREFIX}-catalog-line`)}>
        Your Terms, filings and statements keep “{block.catalog_name}”.
      </p>
    </>
  ) : null;

  return (
    <Card testId={`${PREFIX}-card`}>
      <CardHeader
        title={
          <span ref={headingRef} tabIndex={-1} className="outline-none">
            Name on Givmo
          </span>
        }
        subtitle="The name donors see when they find your organization and give."
      />
      <CardBody className="flex flex-col gap-16">
        {showField ? (
          <form
            className="flex flex-col gap-8"
            noValidate
            onSubmit={onSubmit}
            {...testIdProp(`${PREFIX}-form`)}
          >
            <TextField
              ref={inputRef}
              label="Display name"
              autoComplete="off"
              value={value}
              onChange={(event) => {
                setValue(event.target.value);
                setOutcome(null);
              }}
              error={outcome?.kind === 'refused'}
              errorMessage={outcome?.kind === 'refused' ? outcome.sentence : undefined}
              {...(describedBy ? { 'aria-describedby': describedBy } : {})}
              testId={`${PREFIX}-input`}
            />
            {sameAsCatalog ? (
              <p
                id={sameAsCatalogId}
                role="status"
                className="text-s13 text-text-secondary"
                {...testIdProp(`${PREFIX}-same-as-catalog`)}
              >
                That’s already your organization’s name on Givmo.
              </p>
            ) : null}
            {onlyCatalog ? (
              <p
                id={onlyCatalogId}
                role="status"
                className="text-s13 text-text-secondary"
                {...testIdProp(`${PREFIX}-only-catalog`)}
              >
                Only “{block.catalog_name}” can be saved now.
              </p>
            ) : null}
            {liveLines}
          </form>
        ) : (
          <div className="flex flex-col gap-8">
            <NameRow name={name} />
            {liveLines}
          </div>
        )}

        {canEdit && !block.eligible ? (
          <IneligibleLine
            reason={block.ineligible_reason}
            switchOff={
              endOnly
                ? { id: goBackId, sentence: switchOffSentence(name, block.catalog_name) }
                : null
            }
          />
        ) : null}

        {outcome?.kind === 'held' ? (
          <Notice icon={Info} testId={`${PREFIX}-held`}>
            <p>{outcome.sentence}</p>
            <div>
              <Button
                appearance="saas"
                tone="primary"
                size="sm"
                loading={submitting}
                disabled={busy}
                onClick={() => ask(true, outcome.name)}
                testId={`${PREFIX}-submit-for-review`}
              >
                Submit for review
              </Button>
            </div>
          </Notice>
        ) : null}

        {outcome?.kind === 'unavailable' ? (
          <Notice icon={Info} testId={`${PREFIX}-unavailable`}>
            <p>{outcome.sentence}</p>
          </Notice>
        ) : null}

        {pendingName !== null ? (
          <Notice icon={Clock} testId={`${PREFIX}-pending`}>
            <p className="text-text-strong">“{pendingName}” is waiting for our team’s review.</p>
            {block.pending_reason ? <p>{block.pending_reason}</p> : null}
            {live ? (
              <p {...testIdProp(`${PREFIX}-stays-live`)}>“{name}” stays live meanwhile.</p>
            ) : null}
            {canEdit ? (
              <div>
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  loading={withdraw.isPending}
                  disabled={busy}
                  onClick={() => withdraw.mutate({ path: { charity_id: charityId } })}
                  testId={`${PREFIX}-withdraw`}
                >
                  Withdraw request
                </Button>
              </div>
            ) : null}
          </Notice>
        ) : null}

        {decision !== null ? (
          <Notice icon={MessageSquare} testId={`${PREFIX}-rejected`}>
            <p className="text-text-strong">
              Our team didn’t approve{' '}
              {decision.name !== null ? `“${decision.name}”` : 'your requested name'}
              {decidedOn ? ` (${decidedOn})` : ''}.
            </p>
            {decision.note ? (
              <p {...testIdProp(`${PREFIX}-rejected-note`)}>{decision.note}</p>
            ) : null}
          </Notice>
        ) : null}

        <dl className="grid grid-cols-1 gap-16 sm:grid-cols-2" {...testIdProp(`${PREFIX}-facts`)}>
          <Fact
            label={legalNameLabel(block)}
            value={legalName ?? 'Not on file'}
            testId={`${PREFIX}-legal-name`}
          />
          <Fact label="EIN" value={ein ?? '—'} testId={`${PREFIX}-ein`} />
        </dl>

        {showField ? (
          <div className="flex flex-wrap items-center justify-end gap-8">
            <Button
              appearance="saas"
              tone="primary"
              disabled={!savable || answered || busy}
              loading={save.isPending && !submitting}
              onClick={saveTyped}
              testId={`${PREFIX}-save`}
            >
              Save name
            </Button>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

/**
 * Why the charity cannot choose a display name now, its next step's words linked (the Terms page, the
 * support address). Beside a live name the reason is said its live-name way and ends with how to
 * switch the live name off, the one thing the card then takes.
 */
function IneligibleLine({
  reason,
  switchOff,
}: {
  reason: string | null | undefined;
  switchOff: { id: string; sentence: string } | null;
}) {
  const parts = switchOff ? ineligibleLiveParts(reason) : ineligibleParts(reason);
  return (
    <p className="text-s13 text-text-secondary" {...testIdProp(`${PREFIX}-ineligible`)}>
      {parts.map((part, index) => (
        <SentencePiece key={index} part={part} />
      ))}
      {switchOff ? (
        <span id={switchOff.id} {...testIdProp(`${PREFIX}-go-back`)}>
          {' '}
          {switchOff.sentence}
        </span>
      ) : null}
    </p>
  );
}

const LINK_CLASS = cn(
  'rounded-sm font-w600 text-brand-700 underline underline-offset-2 outline-none hover:text-brand-800',
  FOCUS_RING,
);

/** A reason's words, a console page they lead to, or the address to write to. */
function SentencePiece({ part }: { part: SentencePart }) {
  if (part.kind === 'route') {
    return (
      <Link to={part.to} className={LINK_CLASS} {...testIdProp(`${PREFIX}-ineligible-link`)}>
        {part.text}
      </Link>
    );
  }
  if (part.kind === 'mail') {
    return (
      <a
        href={`mailto:${part.address}`}
        className={LINK_CLASS}
        {...testIdProp(`${PREFIX}-ineligible-mail`)}
      >
        {part.text}
      </a>
    );
  }
  return <>{part.text}</>;
}

/** The name as a Member, or an Owner who cannot change it now, reads it. */
function NameRow({ name }: { name: string }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="font-sans text-s12 font-w600 tracking-givmo text-text-tertiary">
        Name donors see
      </span>
      <span
        className="font-sans text-s16 font-w600 text-text-strong"
        {...testIdProp(`${PREFIX}-name`)}
      >
        {name}
      </span>
    </div>
  );
}

function Fact({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <div className="flex flex-col gap-2">
      <dt className="font-sans text-s12 font-w600 tracking-givmo text-text-tertiary">{label}</dt>
      <dd className="font-sans text-s14 text-text-strong" {...testIdProp(testId)}>
        {value}
      </dd>
    </div>
  );
}

/** The console's quiet notice: the sunken box the read-only note and the domain card use. */
function Notice({
  icon,
  testId,
  children,
}: {
  icon: typeof Info;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="flex items-start gap-10 rounded-lg border border-border-subtle bg-surface-sunken p-14"
      {...testIdProp(testId)}
    >
      <Icon icon={icon} size={18} className="mt-1 shrink-0 text-text-tertiary" aria-hidden />
      <div className="flex min-w-0 flex-col gap-8 text-s13 text-text-secondary">{children}</div>
    </div>
  );
}
